// Enhanced deployment version checking utility with comprehensive cache clearing
export const checkDeploymentVersion = async (): Promise<{ hasUpdate: boolean; newVersion?: string }> => {
  try {
    // Super aggressive cache-busting parameters
    const cacheBuster = `?v=${Date.now()}&cb=${Math.random()}&t=${new Date().getTime()}&bust=${Math.floor(Math.random() * 999999)}`;
    const response = await fetch(`/deploy-version.json${cacheBuster}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
        'Pragma': 'no-cache',
        'Expires': '0',
        'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT',
        // Additional headers to bypass CDN caching
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json, text/plain, */*'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    const currentVersion = document.querySelector('meta[name="deploy-version"]')?.getAttribute('content');
    
    // Log asset information for debugging
    const scripts = Array.from(document.querySelectorAll('script[src]')).map(s => (s as HTMLScriptElement).src);
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => (l as HTMLLinkElement).href);
    
    console.log('🔍 Version check:', { 
      current: currentVersion, 
      remote: data.version, 
      match: currentVersion === data.version,
      timestamp: new Date().toISOString(),
      loadedAssets: { scripts: scripts.length, stylesheets: stylesheets.length }
    });
    
    if (currentVersion !== data.version) {
      console.log('🚨 New deployment detected:', data.version, 'Current:', currentVersion);
      console.log('📦 Current loaded assets:', { scripts, stylesheets });
      return { hasUpdate: true, newVersion: data.version };
    }
    
    return { hasUpdate: false };
  } catch (error) {
    console.error('❌ Failed to check deployment version:', error);
    // Return false to avoid false positives on network errors
    return { hasUpdate: false };
  }
};

// Verify deployment by checking server timestamp
export const verifyDeployment = async (): Promise<{ success: boolean; serverTime?: number }> => {
  try {
    const response = await fetch(`/deploy-version.json?verify=${Date.now()}`, {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' }
    });
    const data = await response.json();
    return { success: true, serverTime: data.timestamp };
  } catch (error) {
    console.error('Deployment verification failed:', error);
    return { success: false };
  }
};

export const clearAllCaches = async (): Promise<void> => {
  try {
    console.log('🧹 Starting comprehensive cache clear...');
    
    // Unregister all service workers first
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(registration => {
          console.log('🔧 Unregistering service worker:', registration.scope);
          return registration.unregister();
        })
      );
    }

    // Clear all browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log('🗑️ Clearing caches:', cacheNames);
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('✅ Service worker caches cleared');
    }

    // Clear browser storage
    if (typeof Storage !== 'undefined') {
      localStorage.clear();
      sessionStorage.clear();
      console.log('✅ Browser storage cleared');
    }

    // Clear IndexedDB if present
    if ('indexedDB' in window) {
      try {
        const databases = await indexedDB.databases();
        await Promise.all(
          databases.map(db => {
            if (db.name) {
              console.log('🗄️ Clearing IndexedDB:', db.name);
              return new Promise<void>((resolve, reject) => {
                const deleteReq = indexedDB.deleteDatabase(db.name!);
                deleteReq.onsuccess = () => resolve();
                deleteReq.onerror = () => reject(deleteReq.error);
              });
            }
            return Promise.resolve();
          })
        );
        console.log('✅ IndexedDB cleared');
      } catch (e) {
        console.warn('⚠️ Could not clear IndexedDB:', e);
      }
    }

    console.log('🎉 All caches and storage cleared successfully');
  } catch (error) {
    console.error('❌ Error clearing caches:', error);
  }
};

export const forceRefresh = async () => {
  console.log('🚀 Starting force refresh sequence...');
  
  // Clear all caches first
  await clearAllCaches();
  
  // Add multiple cache-busting parameters to current URL
  const url = new URL(window.location.href);
  url.searchParams.set('v', Date.now().toString());
  url.searchParams.set('cache-bust', Math.random().toString(36));
  url.searchParams.set('force-refresh', 'true');
  
  console.log('🔄 Forcing hard reload with URL:', url.toString());
  
  // Use location.replace for a true hard reload that bypasses all caches
  window.location.replace(url.toString());
};

export const softRefresh = async () => {
  // Clear caches but use regular reload
  await clearAllCaches();
  window.location.reload();
};